import React from 'react';
import { Avatar, Button } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import ArrowDownward from '@mui/icons-material/ArrowDownward';
import ArrowUpward from '@mui/icons-material/ArrowUpward';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import FollowPlayerButton from '../scouting/FollowPlayerButton';
import { playerKey, playerName } from '../../utils/playerRanking';
import { isGoalkeeper, playerPosition } from '../../utils/playerDiscovery';
import '../playerStatistics/playerRoleBadge.css';
import './directories.css';

export default function PlayerRankingResults({ players, metrics, title, sort, direction, onSort, onSelect,
  selectedId, profileUrl, profileState, displayMetric, cards = false, full = true, offset = 0,
  idPrefix = 'ranking-player', sortLabel }) {
  const { t } = useTranslation('rankings');
  const { t: d } = useTranslation('directories');
  const theme = useTheme();
  const follow = player => <FollowPlayerButton jugadorId={player.idJugador}
    jugadorNombre={playerName(player)} equipoNombre={player.nombreEquipo || player.equipo} size="small" />;
  const nameLink = player => {
    const to = profileUrl(player);
    return to ? <Link className="dr-name" to={to} state={profileState(player)}>{playerName(player)}</Link>
      : <span className="dr-name">{playerName(player)}</span>;
  };
  if (cards) return <div className="dr-player-cards">
    {players.map(player => <article className={`dr-player-card${isGoalkeeper(player) ? ' player-goalkeeper' : ''}`}
      data-theme={theme.palette.mode} key={playerKey(player)}
      id={`${idPrefix}-${playerKey(player)}`} tabIndex={-1}>
      <header><Avatar className="dr-avatar" src={player.foto || undefined} alt="">{player.nombre?.[0]}</Avatar>
        <div>{nameLink(player)}
          {playerPosition(player) && <small className="dr-player-position dr-muted">{playerPosition(player)}</small>}
          <p>{player.nombreEquipo || player.equipo}</p>
        </div>{follow(player)}</header>
      <dl className="dr-kpis">{metrics.map(([key, label]) => <div key={key}>
        <dt>{label}</dt><dd>{displayMetric(player, key)}</dd>
      </div>)}</dl>
      <Button aria-label={d('quickView', { name: playerName(player) })}
        onClick={() => onSelect(playerKey(player))}>{d('viewSummary')}</Button>
    </article>)}
  </div>;
  return <div className="dr-table-scroll" role="region" tabIndex={0} aria-label={title}>
    <table className="dr-table dr-ranking-table" aria-label={title}
      style={{ '--dr-ranking-min': `${550 + metrics.length * 112}px` }}>
      <thead><tr><th scope="col">#</th><th scope="col" aria-label={t('players.table.followAria')} />
        <th scope="col" aria-sort={sort === 'nombre' ? direction === 'asc' ? 'ascending' : 'descending' : 'none'}>
          <button className="dr-sort" disabled={!full} aria-label={sortLabel?.(t('players.table.player'))}
            onClick={() => onSort('nombre')}>{t('players.table.player')}</button></th>
        <th scope="col">{t('players.table.team')}</th>
        {metrics.map(([key, label]) => <th key={key} scope="col" className="dr-number"
          aria-sort={sort === key ? direction === 'asc' ? 'ascending' : 'descending' : 'none'}>
          <button className="dr-sort" disabled={!full} aria-label={sortLabel?.(label)} onClick={() => onSort(key)}>
            {label}{sort === key && (direction === 'asc'
              ? <ArrowUpward aria-hidden="true" /> : <ArrowDownward aria-hidden="true" />)}</button>
        </th>)}{full && <th scope="col">{d('discovery.actions')}</th>}</tr></thead>
      <tbody>{players.map((player, index) => <tr key={playerKey(player)} id={`${idPrefix}-${playerKey(player)}`}
        className={[playerKey(player) === selectedId ? 'dr-selected' : '', isGoalkeeper(player) ? 'player-goalkeeper' : ''].filter(Boolean).join(' ')}
        data-theme={theme.palette.mode}
        aria-selected={playerKey(player) === selectedId} tabIndex={0}
        onClick={event => {
          if (!event.target.closest('a, button, input, select, textarea, [role="button"], [role="checkbox"]')) {
            onSelect(playerKey(player));
          }
        }}
        onKeyDown={event => {
          if (event.target === event.currentTarget && ['Enter', ' '].includes(event.key)) {
            event.preventDefault();
            onSelect(playerKey(player));
          }
        }}>
        <td>{offset + index + 1}</td><td>{follow(player)}</td><td>
          <div className="dr-ranking-identity">
            <Avatar className="dr-avatar" src={player.foto || undefined} alt="">{player.nombre?.[0]}</Avatar>
            <div>{nameLink(player)}
              {playerPosition(player) && <small className="dr-player-position dr-muted">{playerPosition(player)}</small>}
            </div>
          </div>
        </td><td>{player.nombreEquipo || player.equipo || '—'}</td>
        {metrics.map(([key]) => <td className={`dr-number ${key === sort ? 'dr-metric-active' : ''}`}
          key={key}>{displayMetric(player, key)}</td>)}
        {full && <td><div className="pd-row-actions">
          <Button variant="outlined" onClick={() => onSelect(playerKey(player))}
            aria-label={d('discovery.selectNamed', { name: playerName(player) })}>{d('discovery.selectPlayer')}</Button>
        </div></td>}
      </tr>)}</tbody>
    </table>
  </div>;
}
